/**
 * Turing API Service
 * 用于 Preview 中测试真实设备控制
 * 
 * Performance Features:
 * - Request caching with configurable TTL
 * - Batch device queries
 * - Request deduplication
 */

import { getCurrentApiUrl } from './environmentStore'

// 根據當前域名自動判斷後端 URL
function getTuringUrl(): string {
  const override = import.meta.env.VITE_TURING_URL
  // Ignore localhost override unless VITE_USE_LOCAL_BACKEND=true (use staging instead)
  if (override && /localhost|127\.0\.0\.1/i.test(override) && !import.meta.env.VITE_USE_LOCAL_BACKEND) {
    return getCurrentApiUrl()
  }
  if (override) return override
  return getCurrentApiUrl()
}

const TURING_URL = getTuringUrl();

export type TuringAuthContext = {
  apiKey?: string;
  accessToken?: string;
  appId?: string;
};

// ============ Cache Configuration ============

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private pendingRequests = new Map<string, Promise<unknown>>();
  private defaultTTL = 10000; // 10 seconds

  constructor(defaultTTL?: number) {
    if (defaultTTL) this.defaultTTL = defaultTTL;
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  set<T>(key: string, data: T, ttl?: number): void {
    const expiresAt = Date.now() + (ttl ?? this.defaultTTL);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt,
    });
  }

  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  // Deduplicate in-flight requests
  async dedupe<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    const promise = fetcher().finally(() => {
      this.pendingRequests.delete(key);
    });
    
    this.pendingRequests.set(key, promise);
    return promise;
  }

  // Get with automatic fetching and caching
  async getOrFetch<T>(
    key: string, 
    fetcher: () => Promise<T>, 
    ttl?: number
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached) return cached;

    const data = await this.dedupe(key, fetcher);
    this.set(key, data, ttl);
    return data;
  }
}

// Global cache instance
export const apiCache = new RequestCache(10000);

function buildHeaders(apiKey: string, appId?: string): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
  };
  if (appId) {
    headers['Ultron-Cloud-Appid'] = appId;
  }
  return headers;
}

function resolveAuthContext(
  authOrApiKey: string | TuringAuthContext,
  appId?: string
): TuringAuthContext {
  if (typeof authOrApiKey === 'string') {
    return { apiKey: authOrApiKey, appId };
  }
  return {
    apiKey: authOrApiKey.apiKey,
    accessToken: authOrApiKey.accessToken,
    appId: authOrApiKey.appId ?? appId,
  };
}

function buildHeadersFromContext(context: TuringAuthContext): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (context.appId) {
    headers['Ultron-Cloud-Appid'] = context.appId;
  }
  if (context.accessToken) {
    headers.Authorization = `Bearer ${context.accessToken}`;
    return headers;
  }
  if (context.apiKey) {
    headers['x-api-key'] = context.apiKey;
    return headers;
  }
  throw new Error('Missing authentication: provide apiKey or accessToken');
}

// ============ Type Definitions ============

export interface RangeConfig {
  min: number;
  max: number;
}

export interface DeviceAttributes {
  brightnessRange?: RangeConfig;
  colorTemperatureRange?: RangeConfig;
  humidityRange?: RangeConfig;
  temperatureRange?: RangeConfig;
  fanSpeedLevels?: string[];
  supportedModes?: string[];
  [key: string]: unknown;
}

export interface DeviceEndpoint {
  endpoint: string;
  deviceSN: string;
  type: string;
  traits: string[];
  nickname: string;
  attributes: DeviceAttributes;
  availableCommands?: unknown[] | null;
}

export interface QueryDevicesResponse {
  success: boolean;
  type: string;
  data: Array<{
    system: {
      baseSN: string;
      deviceName: string;
      groupId: string;
      groupName: string | null;
      online: boolean;
      lastSeen: string;
      deviceType?: string | null;
    };
    endpoints?: DeviceEndpoint[];
    capabilities?: DeviceEndpoint[]; // 新 API 使用 capabilities
  }>;
  count: number;
}

export interface QueryStatusResponse {
  success: boolean;
  type: string;
  data: Array<{
    system: {
      baseSN: string;
      deviceName: string;
      groupId: string;
      groupName: string | null;
      online: boolean;
      lastSeen: string;
    };
    states: Array<{
      endpoint: string;
      deviceSN: string;
      type: string;
      nickname: string;
      values: Record<string, { value: unknown }>;
    }>;
  }>;
  _source: string;
  timestamp: number;
}

export interface ExecuteCommandResponse {
  success: boolean;
  type: string;
  action: string;
  devices: number;
  result: Array<{
    success: boolean;
    device: string;
    result: {
      valid: boolean;
      error: string | null;
      cmdId: string | null;
    };
  }>;
  smartTip?: string;
}

export type SceneActionParams = {
  brightness?: number;
  temperature?: number;
  mode?: string;
  fanSpeed?: string;
  openPercent?: number;
  colorTemp?: number;
  [key: string]: unknown;
};

export type SceneDeviceCommand = {
  sn: string;
  command: string;
  params?: SceneActionParams;
};

export type ConfigureScenePayload = {
  action: 'query' | 'create' | 'update' | 'delete' | 'run';
  id?: string;
  sceneId?: string;
  name?: string;
  groupId?: string;
  includeDetails?: boolean;
  enabled?: boolean;
  devices?: SceneDeviceCommand[];
};

export interface ConfigureSceneResponse {
  success: boolean;
  type?: string;
  action?: string;
  message?: string;
  sceneId?: string;
  id?: string;
  data?: unknown;
  result?: unknown;
  error?: string;
  [key: string]: unknown;
}

// ============ API Functions ============

// ============ QueryLocations ============

export interface LocationInfo {
  id: string;
  groupId?: string;  // API returns groupId
  name: string;
  groupName?: string;  // API returns groupName
  deviceCount?: number;
}

export interface QueryLocationsResponse {
  success: boolean;
  type: string;
  data: LocationInfo[];
  count: number;
}

/**
 * 查询位置/群组列表 (QueryLocations)
 */
export async function queryLocations(
  apiKey: string,
  appId?: string
): Promise<QueryLocationsResponse> {
  const res = await fetch(`${TURING_URL}/mcp/QueryLocations`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify({}),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 查询设备列表和属性 (QueryDevices)
 * 返回设备的 traits, attributes (包含 brightnessRange, colorTemperatureRange 等)
 */
export async function queryDevices(
  apiKey: string,
  deviceSNs: string[],
  appId?: string
): Promise<QueryDevicesResponse> {
  const res = await fetch(`${TURING_URL}/mcp/QueryDevices`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify({ sns: deviceSNs }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 查询设备列表和属性 (QueryDevices) - API Key / OAuth 共用
 */
export async function queryDevicesWithAuthContext(
  authContext: TuringAuthContext,
  deviceSNs: string[]
): Promise<QueryDevicesResponse> {
  const context = resolveAuthContext(authContext);
  const res = await fetch(`${TURING_URL}/mcp/QueryDevices`, {
    method: 'POST',
    headers: buildHeadersFromContext(context),
    body: JSON.stringify({ sns: deviceSNs }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 根据位置 ID 查询设备列表 (QueryDevices with locationIds)
 * 用于获取账户下所有设备的简要信息
 */
export async function queryDevicesByLocations(
  apiKey: string,
  locationIds: string[],
  appId?: string
): Promise<QueryDevicesResponse> {
  // Use locationId (singular) for single location, locationIds (plural) for multiple
  const body = locationIds.length === 1
    ? { locationId: locationIds[0] }
    : { locationIds };
    
  const res = await fetch(`${TURING_URL}/mcp/QueryDevices`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 查询设备当前状态 (QueryStatus)
 */
export async function queryDeviceStatus(
  apiKey: string,
  deviceSN: string,
  appId?: string
): Promise<QueryStatusResponse> {
  const res = await fetch(`${TURING_URL}/mcp/QueryStatus`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify({ devices: [deviceSN] }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 查询设备当前状态 (QueryStatus) - API Key / OAuth 共用
 */
export async function queryDeviceStatusWithAuthContext(
  authContext: TuringAuthContext,
  deviceSN: string
): Promise<QueryStatusResponse> {
  const context = resolveAuthContext(authContext);
  const res = await fetch(`${TURING_URL}/mcp/QueryStatus`, {
    method: 'POST',
    headers: buildHeadersFromContext(context),
    body: JSON.stringify({ devices: [deviceSN] }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 根據位置 ID 查詢設備當前狀態 (QueryStatus with locationIds)
 */
export async function queryStatusByLocations(
  apiKey: string,
  locationIds: string[],
  appId?: string
): Promise<QueryStatusResponse> {
  const body = locationIds.length === 1
    ? { locationId: locationIds[0] }
    : { locationIds };

  const res = await fetch(`${TURING_URL}/mcp/QueryStatus`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 执行设备控制命令
 */
export async function executeCommand(
  apiKey: string,
  deviceSN: string,
  command: string,
  params?: Record<string, unknown>,
  appId?: string
): Promise<ExecuteCommandResponse> {
  const body: Record<string, unknown> = {
    devices: [deviceSN],
    command,
  };

  if (params) {
    body.params = params;
  }

  const res = await fetch(`${TURING_URL}/mcp/ExecuteCommand`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * ConfigureScene MCP 統一入口（query/create/update/delete/run）
 */
export async function configureScene(
  authOrApiKey: string | TuringAuthContext,
  payload: ConfigureScenePayload,
  appId?: string
): Promise<ConfigureSceneResponse> {
  const authContext = resolveAuthContext(authOrApiKey, appId);
  const res = await fetch(`${TURING_URL}/mcp/ConfigureScene`, {
    method: 'POST',
    headers: buildHeadersFromContext(authContext),
    body: JSON.stringify(payload),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

export async function queryScenes(
  authOrApiKey: string | TuringAuthContext,
  appId?: string,
  includeDetails = false
): Promise<ConfigureSceneResponse> {
  return configureScene(authOrApiKey, { action: 'query', includeDetails }, appId);
}

export async function runScene(
  authOrApiKey: string | TuringAuthContext,
  sceneId: string,
  appId?: string,
  groupId?: string
): Promise<ConfigureSceneResponse> {
  return configureScene(authOrApiKey, { action: 'run', sceneId, groupId }, appId);
}

export async function deleteScene(
  authOrApiKey: string | TuringAuthContext,
  sceneId: string,
  appId?: string,
  groupId?: string
): Promise<ConfigureSceneResponse> {
  return configureScene(authOrApiKey, { action: 'delete', sceneId, groupId }, appId);
}

/**
 * 分析設備日誌 (AnalyzeDeviceLogs)
 * 用於獲取佔用時間軸等歷史數據
 */
export interface AnalyzeDeviceLogsResponse {
  success: boolean;
  type?: string;
  timestamp?: number;
  data?: {
    deviceSN?: string;
    period?: string;
    analysis?: {
      occupancy?: {
        timeline: Array<{
          start: string;
          end: string;
          occupied: boolean;
        }>;
        summary: {
          totalOccupiedMinutes: number;
          totalMinutes: number;
          occupancyRate: number;
        };
      };
      events?: Array<{
        timestamp: string;
        type: string;
        value: unknown;
      }>;
    };
  };
  recentEvents?: Array<{
    timestamp?: number | string;
    time?: string;
    type?: string;
    event?: string;
    category?: string;
    source?: string;
    message?: Record<string, unknown>;
    displayTemplates?: string[];
    details?: Record<string, unknown>;
  }>;
  summary?: unknown;
  analysis?: unknown;
  error?: string;
}

export async function analyzeDeviceLogs(
  apiKey: string,
  deviceSN: string,
  period: 'week' | 'twoWeeks' = 'week',
  appId?: string
): Promise<AnalyzeDeviceLogsResponse> {
  return analyzeDeviceLogsWithAuthContext({ apiKey, appId }, deviceSN, period, 'detailed');
}

export async function analyzeDeviceLogsWithAuthContext(
  authContext: TuringAuthContext,
  deviceSN: string,
  period: 'week' | 'twoWeeks' = 'week',
  format: 'summary' | 'detailed' | 'markdown' = 'detailed'
): Promise<AnalyzeDeviceLogsResponse> {
  const context = resolveAuthContext(authContext);
  const res = await fetch(`${TURING_URL}/mcp/AnalyzeDeviceLogs`, {
    method: 'POST',
    headers: buildHeadersFromContext(context),
    body: JSON.stringify({
      sn: deviceSN,
      period,
      format,
    }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

/**
 * 查詢感測器歷史數據 (GenerateReport)
 * 用於繪製感測器圖表
 */
export interface SensorHistoryDataPoint {
  timestamp: string;
  temp?: number;
  rh?: number;
  pm2_5?: number;
  co2?: number;
  tvoc?: number;
  lux?: number;
  watt?: number;
  watt_hour?: number;
}

export interface SensorHistoryResponse {
  success: boolean;
  data: {
    deviceSN: string;
    report?: {
      hourly?: Array<{
        hour: string;
        data: Record<string, number>;
      }>;
      daily?: Array<{
        date: string;
        data: Record<string, number>;
      }>;
    };
    events?: Array<{
      timestamp: string;
      data: Record<string, number[]>;
    }>;
  };
  timestamp: number;
}

export async function querySensorHistory(
  apiKey: string,
  deviceSN: string,
  duration: '24h' | '7d' | '2w' | '1m' = '24h',
  appId?: string
): Promise<SensorHistoryResponse> {
  // 計算時間範圍
  const endTime = new Date().toISOString();
  
  const res = await fetch(`${TURING_URL}/mcp/GenerateReport`, {
    method: 'POST',
    headers: buildHeaders(apiKey, appId),
    body: JSON.stringify({
      deviceSNs: [deviceSN],
      duration,
      endTime,
      granularity: duration === '24h' ? 'hourly' : 'daily'
    }),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

export type GenerateReportGranularity = 'minute' | 'hour' | 'day' | 'auto'

export interface GenerateReportChartPoint {
  time?: string;
  [key: string]: unknown;
}

export interface GenerateReportDeviceData {
  baseSN?: string;
  deviceSN?: string;
  chartData?: GenerateReportChartPoint[];
  [key: string]: unknown;
}

export interface GenerateReportResponse {
  success: boolean;
  data?: Record<string, GenerateReportDeviceData>;
  metadata?: Record<string, unknown>;
  error?: string;
  message?: string;
}

/**
 * 查詢感測器圖表數據 (GenerateReport) - API Key / OAuth 共用
 */
export async function querySensorReportWithAuthContext(
  authContext: TuringAuthContext,
  deviceSN: string,
  options?: {
    duration?: string;
    endTime?: string;
    metrics?: 'auto' | string[];
    granularity?: GenerateReportGranularity;
    format?: 'summary' | 'detailed';
    utcOffset?: string;
  }
): Promise<GenerateReportResponse> {
  const context = resolveAuthContext(authContext);
  const body: Record<string, unknown> = {
    deviceSNs: [deviceSN],
    duration: options?.duration ?? '24h',
    metrics: options?.metrics ?? 'auto',
    granularity: options?.granularity ?? 'auto',
    format: options?.format ?? 'summary',
  };
  if (options?.endTime) body.endTime = options.endTime;
  if (options?.utcOffset) body.utcOffset = options.utcOffset;

  const res = await fetch(`${TURING_URL}/mcp/GenerateReport`, {
    method: 'POST',
    headers: buildHeadersFromContext(context),
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
  }

  return res.json();
}

// ============ Helper Functions ============

/**
 * 从 QueryDevices 响应中提取设备属性 (traits, attributes)
 * API 回傳結構使用 "capabilities" 而非 "endpoints"
 */
export function extractDeviceAttributes(
  response: QueryDevicesResponse,
  deviceSN: string
): { traits: string[]; attributes: DeviceAttributes } | null {
  if (!response.success || !response.data?.length) {
    return null;
  }

  for (const device of response.data) {
    // 嘗試 capabilities (新 API) 或 endpoints (舊 API)
    const capabilitiesArray = device.capabilities || device.endpoints || [];
    const endpoint = capabilitiesArray.find((e) => e.deviceSN === deviceSN);
    if (endpoint) {
      return {
        traits: endpoint.traits || [],
        attributes: endpoint.attributes || {},
      };
    }
  }

  return null;
}

/**
 * 从 QueryStatus 响应中提取设备状态值
 */
export function extractDeviceState(
  response: QueryStatusResponse,
  deviceSN: string
): Record<string, unknown> | null {
  if (!response.success || !response.data?.length) {
    return null;
  }

  for (const device of response.data) {
    const state = device.states?.find((s) => s.deviceSN === deviceSN);
    if (state?.values) {
      // 将 { key: { value: x } } 转换为 { key: x }
      const values: Record<string, unknown> = {};
      for (const [key, obj] of Object.entries(state.values)) {
        values[key] = obj.value;
      }
      return values;
    }
  }

  return null;
}

/**
 * 获取 trait 的默认范围配置
 */
export function getDefaultRanges(): Record<string, RangeConfig> {
  return {
    brightness: { min: 0, max: 100 },
    colorTemperature: { min: 2700, max: 6500 },
    temperature: { min: 16, max: 30 },
    humidity: { min: 30, max: 80 },
  };
}

/**
 * 合并设备属性与默认范围
 */
export function mergeWithDefaults(
  attributes: DeviceAttributes | null
): Record<string, RangeConfig> {
  const defaults = getDefaultRanges();
  
  if (!attributes) {
    return defaults;
  }

  return {
    brightness: attributes.brightnessRange || defaults.brightness,
    colorTemperature: attributes.colorTemperatureRange || defaults.colorTemperature,
    temperature: attributes.temperatureRange || defaults.temperature,
    humidity: attributes.humidityRange || defaults.humidity,
  };
}

// ============ Batch Query Functions ============

/**
 * 批次查詢多個設備狀態
 * 比多次單獨查詢更高效
 */
export async function queryDevicesStatusBatch(
  apiKey: string,
  deviceSNs: string[],
  appId?: string
): Promise<QueryStatusResponse> {
  const cacheKey = `status:batch:${deviceSNs.sort().join(',')}`;
  
  return apiCache.getOrFetch(cacheKey, async () => {
    const res = await fetch(`${TURING_URL}/mcp/QueryStatus`, {
      method: 'POST',
      headers: buildHeaders(apiKey, appId),
      body: JSON.stringify({ devices: deviceSNs }),
      mode: 'cors',
      credentials: 'omit',
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`API Error: ${res.status}${text ? ` - ${text}` : ''}`);
    }

    return res.json();
  }, 5000); // 5 second TTL for status
}

/**
 * 帶快取的設備查詢
 */
export async function queryDevicesCached(
  apiKey: string,
  deviceSNs: string[],
  appId?: string
): Promise<QueryDevicesResponse> {
  const cacheKey = `devices:${deviceSNs.sort().join(',')}`;
  
  return apiCache.getOrFetch(cacheKey, async () => {
    return queryDevices(apiKey, deviceSNs, appId);
  }, 60000); // 1 minute TTL for device info
}

/**
 * 帶快取的設備狀態查詢
 */
export async function queryDeviceStatusCached(
  apiKey: string,
  deviceSN: string,
  appId?: string
): Promise<QueryStatusResponse> {
  const cacheKey = `status:${deviceSN}`;
  
  return apiCache.getOrFetch(cacheKey, async () => {
    return queryDeviceStatus(apiKey, deviceSN, appId);
  }, 5000); // 5 second TTL
}

/**
 * 執行命令後使狀態快取失效
 */
export async function executeCommandWithInvalidation(
  apiKey: string,
  deviceSN: string,
  command: string,
  params?: Record<string, unknown>,
  appId?: string
): Promise<ExecuteCommandResponse> {
  const result = await executeCommand(apiKey, deviceSN, command, params, appId);
  
  // Invalidate cache for this device on successful command
  if (result.success) {
    apiCache.invalidate(`status:${deviceSN}`);
  }
  
  return result;
}

// ============ Lazy Loading Utilities ============

/**
 * 分頁加載設備列表
 */
export async function queryDevicesPaginated(
  apiKey: string,
  locationIds: string[],
  page: number,
  pageSize: number,
  appId?: string
): Promise<{ devices: QueryDevicesResponse['data']; hasMore: boolean }> {
  const response = await queryDevicesByLocations(apiKey, locationIds, appId);
  
  if (!response.success || !response.data) {
    return { devices: [], hasMore: false };
  }
  
  const start = page * pageSize;
  const end = start + pageSize;
  const devices = response.data.slice(start, end);
  const hasMore = end < response.data.length;
  
  return { devices, hasMore };
}

/**
 * 預加載設備資料
 * 在後台預先載入可能需要的設備資料
 */
export function prefetchDevices(
  apiKey: string,
  deviceSNs: string[],
  appId?: string
): void {
  // Fire and forget - preload into cache
  queryDevicesCached(apiKey, deviceSNs, appId).catch(() => {
    // Ignore prefetch errors
  });
}

/**
 * 清除所有快取
 */
export function clearCache(): void {
  apiCache.invalidate();
}

/**
 * 清除特定設備的快取
 */
export function invalidateDeviceCache(deviceSN: string): void {
  apiCache.invalidate(deviceSN);
}

/**
 * Zone configuration type for dashboard preview
 */
export type ZoneConfig = {
  tabs: { id: string; name: string }[];
  devices: Record<string, string[]>;
};

/**
 * Get tenant settings including tenantId
 */
export async function getTenantSettings(
  apiKey: string,
  appId?: string
): Promise<{ success: boolean; data?: { tenantId: string; timezone?: string; language?: string }; error?: string }> {
  const url = `${TURING_URL}/api/tenant/settings`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: buildHeaders(apiKey, appId),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = await response.json();
    return { success: true, data: data.data };
  } catch (error) {
    console.error('[TuringAPI] getTenantSettings error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Get dashboard zones for the tenant
 */
export async function getDashboardZones(
  apiKey: string,
  appId?: string
): Promise<{ success: boolean; data?: { tenantId: string; locationZones: Record<string, ZoneConfig> }; error?: string }> {
  const url = `${TURING_URL}/api/tenant/dashboard-zones`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: buildHeaders(apiKey, appId),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = await response.json();
    return { success: true, data: data.data };
  } catch (error) {
    console.error('[TuringAPI] getDashboardZones error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Update dashboard zones for the tenant
 */
export async function updateDashboardZones(
  apiKey: string,
  locationZones: Record<string, ZoneConfig>,
  appId?: string
): Promise<{ success: boolean; data?: { tenantId: string; locationZones: Record<string, ZoneConfig> }; error?: string }> {
  const url = `${TURING_URL}/api/tenant/dashboard-zones`;

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: buildHeaders(apiKey, appId),
      body: JSON.stringify({ locationZones }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || `HTTP ${response.status}` };
    }

    const data = await response.json();
    return { success: true, data: data.data };
  } catch (error) {
    console.error('[TuringAPI] updateDashboardZones error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}
